import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CompanyHero from './components/CompanyHero';
import CompanyCulture from './components/CompanyCulture';
import BenefitsOverview from './components/BenefitsOverview';
import EmployeeTestimonials from './components/EmployeeTestimonials';
import OnboardingProgress from './components/OnboardingProgress';
import CTASection from './components/CTASection';
import CompanySelector from './components/CompanySelector';
import AppImage from '../../components/AppImage';
import { motion } from 'framer-motion';

const CompanySpecificLandingPages = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyId = searchParams?.get('company') || 'eppme';

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const companies = [
  {
    id: 'eppme',
    name: 'Eppme Digital Technologies Ltd',
    tagline: 'Innovating Digital Solutions',
    heroTitle: 'Welcome to the Future of Digital Innovation',
    heroDescription: 'Join a dynamic team of innovators, creators, and problem-solvers who are shaping the digital landscape. At Eppme, we believe in empowering young talent to drive technological transformation across Africa.',
    heroImage: "https://img.rocket.new/generatedImages/rocket_gen_img_171170bd3-1764687877901.png",
    heroImageAlt: 'Diverse team of young professionals collaborating in modern office with laptops and digital screens showing innovative technology solutions',
    logo: "/assets/images/eppme_digital_logo-1765300121095.png",
    logoAlt: "Blue and red company logo featuring edt in lowercase blue letters with red accent in top right corner, company name EPPME DIGITAL TECHNOLOGIES in blue capital letters below the main logo design",
    stats: {
      employees: 150,
      projects: 200
    },
    cultureValues: [
    {
      icon: 'Lightbulb',
      iconColor: '#3B82F6',
      bgColor: 'bg-blue-50',
      title: 'Innovation First',
      description: 'We encourage creative thinking and embrace new ideas that push boundaries and challenge the status quo.'
    },
    {
      icon: 'Users',
      iconColor: '#8B5CF6',
      bgColor: 'bg-violet-50',
      title: 'Collaborative Spirit',
      description: 'Teamwork drives our success. We believe in the power of diverse perspectives working together.'
    },
    {
      icon: 'TrendingUp',
      iconColor: '#10B981',
      bgColor: 'bg-emerald-50',
      title: 'Continuous Growth',
      description: 'Your development is our priority. We invest in training, mentorship, and career advancement opportunities.'
    },
    {
      icon: 'Heart',
      iconColor: '#DC2626',
      bgColor: 'bg-red-50',
      title: 'Work-Life Balance',
      description: 'We value your well-being and promote a healthy balance between professional and personal life.'
    }],

    benefits: [
    {
      icon: 'Wallet',
      iconColor: '#10B981',
      bgColor: 'bg-emerald-50',
      title: 'Competitive Salary',
      description: 'Industry-leading compensation packages with performance-based bonuses and annual reviews.'
    },
    {
      icon: 'GraduationCap',
      iconColor: '#3B82F6',
      bgColor: 'bg-blue-50',
      title: 'Learning & Development',
      description: 'Access to online courses, certifications, workshops, and conference attendance opportunities.'
    },
    {
      icon: 'Laptop',
      iconColor: '#8B5CF6',
      bgColor: 'bg-violet-50',
      title: 'Modern Equipment',
      description: 'Latest technology and tools to help you perform at your best, including MacBooks and premium software.'
    },
    {
      icon: 'Clock',
      iconColor: '#F59E0B',
      bgColor: 'bg-amber-50',
      title: 'Flexible Hours',
      description: 'Work-life balance with flexible scheduling and remote work options for eligible positions.'
    },
    {
      icon: 'Shield',
      iconColor: '#DC2626',
      bgColor: 'bg-red-50',
      title: 'Health Insurance',
      description: 'Comprehensive health coverage for you and your family, including dental and vision care.'
    },
    {
      icon: 'Coffee',
      iconColor: '#059669',
      bgColor: 'bg-emerald-50',
      title: 'Office Perks',
      description: 'Free meals, snacks, recreational facilities, and regular team-building activities.'
    }],

    testimonials: [
    {
      name: 'Chidinma Okafor',
      role: 'Frontend Developer',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5b689fb-1763296744210.png",
      avatarAlt: 'Professional headshot of young Nigerian woman with braided hair wearing blue blazer smiling confidently',
      quote: 'Starting my career at Eppme has been transformative. The mentorship program helped me grow from a corps member to a confident developer in just one year.',
      joinedDate: 'January 2024'
    },
    {
      name: 'Oluwaseun Adebayo',
      role: 'Product Designer',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d0e5ef1f-1763292250024.png",
      avatarAlt: 'Professional headshot of young Nigerian man with short hair wearing white shirt in modern office setting',
      quote: 'The collaborative environment and cutting-edge projects make every day exciting. I\'ve worked on solutions that impact millions of users across Africa.',
      joinedDate: 'March 2024'
    },
    {
      name: 'Blessing Nwankwo',
      role: 'Data Analyst',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1da2ecebc-1763295630273.png",
      avatarAlt: 'Professional headshot of young Nigerian woman with natural hair wearing green top with warm smile',
      quote: 'Eppme invested in my professional development from day one. The learning opportunities and supportive team culture exceeded all my expectations.',
      joinedDate: 'November 2023'
    }]

  },
  {
    id: 'zetavista',
    name: 'Zetavista Analytics',
    tagline: 'Data-Driven Excellence',
    heroTitle: 'Transform Data into Strategic Insights',
    heroDescription: 'Be part of a team that turns complex data into actionable intelligence. At Zetavista, we empower businesses with analytics solutions that drive real-world impact and innovation.',
    heroImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1ff08559a-1765030143856.png",
    heroImageAlt: 'Modern analytics team working with data visualizations on multiple screens in contemporary office with glass walls',
    logo: "/assets/images/tmps0j2w43m-1765350468642.jpg",
    logoAlt: "Modern Zetavista logo featuring the company name ZETAVISTA in bold black letters with ANALYTICS in smaller text below, accompanied by a stylized green bar chart icon on the right side representing data analytics and business intelligence services",
    stats: {
      employees: 85,
      projects: 150
    },
    cultureValues: [
    {
      icon: 'BarChart3',
      iconColor: '#3B82F6',
      bgColor: 'bg-blue-50',
      title: 'Data Excellence',
      description: 'We pursue precision and accuracy in every analysis, ensuring our insights drive meaningful business decisions.'
    },
    {
      icon: 'Brain',
      iconColor: '#8B5CF6',
      bgColor: 'bg-violet-50',
      title: 'Analytical Thinking',
      description: 'Critical thinking and problem-solving are at the core of everything we do, from data collection to presentation.'
    },
    {
      icon: 'Target',
      iconColor: '#10B981',
      bgColor: 'bg-emerald-50',
      title: 'Client Focus',
      description: 'Understanding client needs and delivering solutions that exceed expectations is our primary mission.'
    },
    {
      icon: 'Zap',
      iconColor: '#F59E0B',
      bgColor: 'bg-amber-50',
      title: 'Agile Approach',
      description: 'We adapt quickly to changing requirements and embrace iterative improvements in our methodologies.'
    }],

    benefits: [
    {
      icon: 'DollarSign',
      iconColor: '#10B981',
      bgColor: 'bg-emerald-50',
      title: 'Premium Compensation',
      description: 'Top-tier salaries with quarterly bonuses based on project success and individual performance metrics.'
    },
    {
      icon: 'BookOpen',
      iconColor: '#3B82F6',
      bgColor: 'bg-blue-50',
      title: 'Analytics Training',
      description: 'Specialized training in advanced analytics tools, machine learning, and data visualization platforms.'
    },
    {
      icon: 'Monitor',
      iconColor: '#8B5CF6',
      bgColor: 'bg-violet-50',
      title: 'Premium Tools',
      description: 'Access to industry-leading analytics software, cloud computing resources, and data platforms.'
    },
    {
      icon: 'Home',
      iconColor: '#F59E0B',
      bgColor: 'bg-amber-50',
      title: 'Remote Options',
      description: 'Hybrid work model with flexibility to work remotely while maintaining collaborative team dynamics.'
    },
    {
      icon: 'Heart',
      iconColor: '#DC2626',
      bgColor: 'bg-red-50',
      title: 'Wellness Program',
      description: 'Comprehensive health benefits including gym memberships, mental health support, and wellness initiatives.'
    },
    {
      icon: 'Award',
      iconColor: '#059669',
      bgColor: 'bg-emerald-50',
      title: 'Recognition',
      description: 'Regular recognition programs, employee awards, and career advancement opportunities based on merit.'
    }],

    testimonials: [
    {
      name: 'Emeka Okonkwo',
      role: 'Senior Data Analyst',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1962f21c1-1763293730781.png",
      avatarAlt: 'Professional headshot of young Nigerian man with glasses wearing navy suit in corporate setting',
      quote: 'Zetavista provided the perfect environment to develop my analytical skills. Working with Fortune 500 clients has been an incredible learning experience.',
      joinedDate: 'February 2024'
    },
    {
      name: 'Funmilayo Ajayi',
      role: 'Business Intelligence Specialist',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd18c57d-1763297550621.png",
      avatarAlt: 'Professional headshot of young Nigerian woman with short natural hair wearing professional attire with confident expression',
      quote: 'The mentorship and exposure to cutting-edge analytics tools accelerated my career growth beyond my expectations. Best decision I ever made.',
      joinedDate: 'April 2024'
    },
    {
      name: 'Ibrahim Mohammed',
      role: 'Data Scientist',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d0e5ef1f-1763292250024.png",
      avatarAlt: 'Professional headshot of young Nigerian man with short beard wearing white shirt in modern office environment',
      quote: 'From day one, I was working on real projects that matter. The team\'s expertise and collaborative spirit make complex challenges enjoyable to solve.',
      joinedDate: 'December 2023'
    }]

  },
  {
    id: 'degrey',
    name: 'DeGrey Digital Technologies Ltd',
    tagline: 'Creative Digital Solutions',
    heroTitle: 'Where Creativity Meets Technology',
    heroDescription: 'Join a creative powerhouse that blends design thinking with technical excellence. At DeGrey, we craft digital experiences that captivate audiences and deliver measurable results.',
    heroImage: "https://img.rocket.new/generatedImages/rocket_gen_img_15afff644-1764641554170.png",
    heroImageAlt: 'Creative team brainstorming with colorful sticky notes on glass wall in bright modern workspace with design sketches',
    logo: "/assets/images/DeGrey_logo-1765311570098.png",
    logoAlt: "Modern geometric logo featuring angular triangular shapes in grey, orange, and red colors forming an abstract design, with company name DeGREY DIGITAL TECHNOLOGIES LIMITED in bold black text",
    stats: {
      employees: 120,
      projects: 180
    },
    cultureValues: [
    {
      icon: 'Palette',
      iconColor: '#3B82F6',
      bgColor: 'bg-blue-50',
      title: 'Creative Freedom',
      description: 'We encourage bold ideas and artistic expression, giving you the freedom to explore innovative design solutions.'
    },
    {
      icon: 'Rocket',
      iconColor: '#8B5CF6',
      bgColor: 'bg-violet-50',
      title: 'Innovation Drive',
      description: 'Pushing boundaries and experimenting with new technologies is part of our DNA and daily practice.'
    },
    {
      icon: 'Users2',
      iconColor: '#10B981',
      bgColor: 'bg-emerald-50',
      title: 'Team Synergy',
      description: 'Designers, developers, and strategists work seamlessly together to create exceptional digital products.'
    },
    {
      icon: 'Star',
      iconColor: '#F59E0B',
      bgColor: 'bg-amber-50',
      title: 'Quality Focus',
      description: 'Excellence in execution and attention to detail define every project we deliver to our clients.'
    }],

    benefits: [
    {
      icon: 'Banknote',
      iconColor: '#10B981',
      bgColor: 'bg-emerald-50',
      title: 'Attractive Packages',
      description: 'Competitive salaries with project-based incentives and annual performance bonuses.'
    },
    {
      icon: 'Sparkles',
      iconColor: '#3B82F6',
      bgColor: 'bg-blue-50',
      title: 'Creative Tools',
      description: 'Access to Adobe Creative Cloud, Figma, premium design resources, and latest creative software.'
    },
    {
      icon: 'Briefcase',
      iconColor: '#8B5CF6',
      bgColor: 'bg-violet-50',
      title: 'Portfolio Projects',
      description: 'Work on diverse, award-winning projects that enhance your portfolio and professional reputation.'
    },
    {
      icon: 'Calendar',
      iconColor: '#F59E0B',
      bgColor: 'bg-amber-50',
      title: 'Flexible Schedule',
      description: 'Creative-friendly work hours that accommodate your peak productivity times and personal commitments.'
    },
    {
      icon: 'Umbrella',
      iconColor: '#DC2626',
      bgColor: 'bg-red-50',
      title: 'Full Coverage',
      description: 'Comprehensive insurance packages covering health, life, and professional liability.'
    },
    {
      icon: 'PartyPopper',
      iconColor: '#059669',
      bgColor: 'bg-emerald-50',
      title: 'Team Events',
      description: 'Regular creative workshops, team outings, industry events, and networking opportunities.'
    }],

    testimonials: [
    {
      name: 'Adaeze Eze',
      role: 'UI/UX Designer',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_16b45f08d-1763297745212.png",
      avatarAlt: 'Professional headshot of young Nigerian woman with long braids wearing colorful top with creative expression',
      quote: 'DeGrey gave me the platform to express my creativity while working on projects for international brands. The learning curve has been amazing.',
      joinedDate: 'January 2024'
    },
    {
      name: 'Chukwuemeka Nnamdi',
      role: 'Full Stack Developer',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d0e5ef1f-1763292250024.png",
      avatarAlt: 'Professional headshot of young Nigerian man with short hair wearing casual shirt in creative office space',
      quote: 'The perfect blend of creative freedom and technical challenges. Every project pushes me to learn new technologies and improve my craft.',
      joinedDate: 'March 2024'
    },
    {
      name: 'Ngozi Okoro',
      role: 'Digital Marketing Specialist',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5b689fb-1763296744210.png",
      avatarAlt: 'Professional headshot of young Nigerian woman with natural afro wearing professional attire with bright smile',
      quote: 'Working at DeGrey has transformed my approach to digital marketing. The collaborative environment and innovative projects keep me motivated daily.',
      joinedDate: 'October 2023'
    }]

  }];


  useEffect(() => {
    const company = companies?.find((c) => c?.id === companyId);
    setSelectedCompany(company || companies?.[0]);
  }, [companyId]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleStartRegistration = () => {
    navigate(`/progressive-registration-form?company=${selectedCompany?.id}`);
  };

  const handleCompanyChange = (newCompanyId) => {
    navigate(`/company-specific-landing-pages?company=${newCompanyId}`);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!selectedCompany) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading company information...</p>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Dynamic Background Pattern - More visible */}
      <div className="fixed inset-0 bg-pattern-dots opacity-25 pointer-events-none"></div>
      {/* Company Brand Gradient - Enhanced */}
      <div 
        className="fixed inset-0 opacity-12 pointer-events-none"
        style={{ 
          background: selectedCompany?.brandColor 
            ? `linear-gradient(135deg, ${selectedCompany?.brandColor}20 0%, transparent 100%)`
            : 'linear-gradient(135deg, var(--color-primary) 0%, transparent 100%)'
        }}
      ></div>
      <div className="relative z-10">
        {/* Header */}
        <motion.header
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 bg-card/85 backdrop-blur-md border-b border-border shadow-brand"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-3">
                <AppImage 
                  src="/assets/images/NYSC_logo-1765293658432.png" 
                  alt="National Youth Service Corps (NYSC) official logo"
                  className="w-10 h-10 object-contain"
                />
                <div>
                  <h1 className="text-lg font-bold text-foreground">CorperConnect</h1>
                  <p className="text-xs text-muted-foreground">Your Career Journey Starts Here</p>
                </div>
              </div>

              <nav className="hidden md:flex items-center gap-6">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/company-selection-hub')}
                  iconName="ArrowLeft"
                  iconPosition="left">

                  Back to Companies
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/employee-profile-hub')}>

                  My Profile
                </Button>
                <Button
                  variant="default"
                  onClick={handleStartRegistration}
                  iconName="ArrowRight"
                  iconPosition="right">

                  Start Registration
                </Button>
              </nav>

              <Button
                variant="ghost"
                size="icon"
                className="md:hidden">

                <Icon name="Menu" size={24} />
              </Button>
            </div>
          </div>
        </motion.header>
        {/* Main Content */}
        <main className="relative z-10">
          {/* Company Selector */}
          <CompanySelector
            companies={companies}
            selectedCompany={selectedCompany}
            onCompanyChange={handleCompanyChange} />


          {/* Onboarding Progress */}
          <OnboardingProgress currentStep={2} />

          {/* Hero Section */}
          <CompanyHero
            company={selectedCompany}
            onStartRegistration={handleStartRegistration} />


          {/* Company Culture */}
          <CompanyCulture company={selectedCompany} />

          {/* Benefits Overview */}
          <BenefitsOverview company={selectedCompany} />

          {/* Employee Testimonials */}
          <EmployeeTestimonials company={selectedCompany} />

          {/* CTA Section */}
          <CTASection
            company={selectedCompany}
            onStartRegistration={handleStartRegistration} />


          {/* Footer */}
          <footer className="bg-card border-t border-border py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid md:grid-cols-4 gap-8 mb-8">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                      <Icon name="Zap" size={18} color="white" />
                    </div>
                    <span className="font-bold text-foreground">CorperConnect</span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Connecting talented corps members with leading tech companies across Nigeria.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-4">Quick Links</h3>
                  <ul className="space-y-2">
                    <li>
                      <button
                        onClick={() => navigate('/company-selection-hub')}
                        className="text-sm text-muted-foreground hover:text-primary transition-colors">

                        All Companies
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => navigate('/progressive-registration-form')}
                        className="text-sm text-muted-foreground hover:text-primary transition-colors">

                        Registration Form
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => navigate('/employee-profile-hub')}
                        className="text-sm text-muted-foreground hover:text-primary transition-colors">

                        Employee Portal
                      </button>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-4">Support</h3>
                  <ul className="space-y-2">
                    <li>
                      <a href="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                        Help Center
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                        Contact Us
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                        FAQs
                      </a>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-4">Legal</h3>
                  <ul className="space-y-2">
                    <li>
                      <a href="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                        Privacy Policy
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                        Terms of Service
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                        Data Protection
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
                <p className="text-sm text-muted-foreground">
                  © {new Date()?.getFullYear()} CorperConnect. All rights reserved.
                </p>
                <div className="flex items-center gap-4">
                  <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                    <Icon name="Twitter" size={20} />
                  </a>
                  <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                    <Icon name="Linkedin" size={20} />
                  </a>
                  <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                    <Icon name="Instagram" size={20} />
                  </a>
                </div>
              </div>
            </div>
          </footer>

          {/* Scroll to Top Button */}
          {showScrollTop &&
          <button
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 w-12 h-12 bg-primary text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center z-50 hover:scale-110"
            aria-label="Scroll to top">

              <Icon name="ArrowUp" size={24} color="white" />
            </button>
          }
        </main>
      </div>
    </div>
  );
};

export default CompanySpecificLandingPages;